// eslint-disable-next-line no-restricted-imports
import { useWatch } from 'react-hook-form';

import type { TypedFieldName, TypedFieldNameValue } from './type';

/**
 * A custom hook that watches the value of a specific field in a form.
 * This hook wraps the `useWatch` hook from react-hook-form and provides stronger typing.
 *
 * @template FieldType The type of the field value you're watching.
 * @template FormValues The type of the overall form values (optional, defaults to void).
 *
 * @param {TypedFieldName<FormValues>} [name] The name of the field to watch.
 * If omitted, it will watch all fields.
 *
 * @returns {FieldType} The current value of the watched field.
 *
 * @example
 * ```tsx
 * import { useWatchFieldValue } from './useWatchFieldValue';
 *
 * interface FormData {
 *   email: string;
 *   age: number;
 * }
 *
 * const EmailDisplay = () => {
 *   const email = useWatchFieldValue<string, FormData>('email');
 *   return <p>Current email: {email}</p>;
 * };
 *
 */
export function useWatchFieldValue<FieldType, FormValues = void>(
    name?: TypedFieldName<FormValues>,
): FieldType {
    return useWatch({ name: name as string });
}

export const createTypedUseWatchFieldValue = <FormValues>() => {
    return <Name extends TypedFieldName<FormValues>>(
        name: Name,
    ): TypedFieldNameValue<FormValues, Name> =>
        useWatchFieldValue<TypedFieldNameValue<FormValues, Name>, FormValues>(name);
};
